/**
 * Admin-only segmented control that lets the admin live-edit a tracker's or
 * snapshot's chart_type from the result page itself.
 *
 * Persists immediately via PATCH /api/admin/trackers/:id/chart-type
 *                       or PATCH /api/admin/questions/:id/chart-type
 *
 * Hidden for non-admin viewers.
 */
import { useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const TYPES = ["line", "bar", "area", "pie", "donut", "radial"];

export default function ChartTypeToggle({ kind, id, current, onChange }) {
  // kind: "tracker" | "question"
  const { user } = useAuth();
  const [value, setValue] = useState(current || "line");
  const [busy, setBusy] = useState(false);

  if (!user || user.role !== "admin") return null;

  const set = async (ct) => {
    if (ct === value) return;
    setBusy(true);
    try {
      const path = kind === "tracker" ? `/admin/trackers/${id}/chart-type` : `/admin/questions/${id}/chart-type`;
      await api.patch(path, { chart_type: ct });
      setValue(ct);
      toast.success(`Chart type → ${ct}`);
      onChange && onChange(ct);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="inline-flex items-center gap-1 bg-white border border-neutral-300 px-1 py-1 text-xs" data-testid="chart-type-toggle" title="Admin · live edit chart style">
      <span className="px-1 text-neutral-500 font-mono uppercase tracking-wide">Chart</span>
      {TYPES.map((t) => (
        <button
          key={t}
          onClick={() => set(t)}
          disabled={busy}
          data-testid={`chart-type-${t}`}
          className={`px-2 py-1 capitalize ${value === t ? "bg-black text-white" : "hover:bg-neutral-100"}`}
        >{t}</button>
      ))}
    </div>
  );
}
